<template>
  <fieldset
    :id="pageId"
    v-loading="isShowLoadingSpinner"
    class="ordering-address-form"
    element-loading-custom-class="custom-spinner-class"
    :element-loading-text="loadingMessage"
  >
    <div class="section-header">
      <h6 class="sub-section-title">
        <slot name="title" />
      </h6>
      <button
        v-if="isShowClose"
        title="Close"
        type="button"
        class="section__headerbtn close-btn"
        :disabled="isDisabled"
        @click="closeAddressSection"
      >
        <i class="fa fa-close" />
      </button>

      <button
        v-if="isDraggable"
        type="button"
        class="section__headerbtn drag-btn"
        :disabled="isDisabled"
        @mouseleave="dragBtnRelease"
        @mouseover="dragBtnOver"
      >
        <i class="fa fa-list" />
      </button>

      <button
        v-if="isShowForceUpdate"
        type="button"
        title="Force update to this stop"
        class="section__headerbtn force-btn"
        :disabled="isDisabled"
        @click="forceUpdateStop"
      >
        <i class="fa fa-flag-checkered" />
      </button>
    </div>

    <slot name="google-map" />

    <p
      v-if="isWarningShow"
      class="venues-notification text-center pb-10"
    >
      Please double check the pin on the map to make sure it's at the right pick up location
    </p>

    <custom-location-buttons
      v-if="isCustomLocationShow"
      ref="customLocationButtons"
      :parent-id="pageId"
      :disabled="isDisabled"
      :map-pin-show="true"
      :map-pin-type="mapPinType"
      :map-pin-index="mapPinIndex"
      :make-marker-clickable="makeMarkerClickable"
      @load-address="loadAddress"
      @change="changeAddress"
      @show-location-name-dialog="showUpdateCustomLocationNameDialog"
      @update-map-from-marker-click="updateMapFromMarkerClick"
    />

    <div class="row align-items-center custom-location-buttons">
      <label class="col-sm-3 control-label"> Street Address1* </label>
      <div class="col-sm-8">
        <address-autocomplete-input
          :id="`streetAddress-${pageId}`"
          v-model="address.streetAddress"
          :required="isAddressRequired"
          :disabled="isDisabled"
          :anchor-bound="anchorBound || customerAnchorBound"
          @place-changed="setAddressData"
        />
      </div>

      <div
        v-if="isCopyAddressAllow"
        class="col-sm-1 col-2 button-group pl-0"
      >
        <div class="location-button">
          <button
            type="button"
            class="btn copy-btn"
            @click="copyDataFromAddressForm(0)"
          >
            <i class="fa fa-copy" />
          </button>
        </div>
      </div>
    </div>

    <div class="row align-items-center custom-location-buttons">
      <label class="col-sm-3 control-label"> Apt # </label>

      <div class="col-sm-5">
        <sanitized-input
          v-model="address.streetAddress2"
          :disabled="isDisabled"
          placeholder="Apt number"
        />
      </div>
    </div>

    <div
      v-if="address.type"
      class="row align-items-center"
    >
      <label class="col-sm-3 control-label"> Street Address2* </label>
      <div class="col-sm-8">
        <sanitized-input
          :id="`streetAddress1-${pageId}`"
          v-model="address.streetAddress1"
          :disabled="isDisabled"
          :required="isAddressRequired"
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label"> City* </label>
      <div class="col-sm-8 col-10">
        <sanitized-input
          :id="`city-${pageId}`"
          v-model="address.city"
          placeholder="Los Angeles"
          :disabled="isDisabled"
          :required="isAddressRequired"
        />
      </div>
    </div>

    <div class="row align-items-center">
      <label class="col-sm-3 control-label">
        {{ isFromCanada ? 'Provinces*' : 'State*' }}
      </label>
      <div class="col-sm-3 col-6">
        <sanitized-input
          v-model="address.state"
          placeholder="CA"
          :disabled="isDisabled"
          :required="isAddressRequired"
        />
      </div>

      <label class="col-sm-2 control-label mobile-mt-10">
        {{ isFromCanada ? 'Postal*' : 'Zip*' }}
      </label>
      <div class="col-sm-3 col-6">
        <sanitized-input
          v-model="address.zip"
          placeholder="90012"
          :disabled="isDisabled"
          :required="isAddressRequired"
        />
      </div>
    </div>

    <div
      v-if="isGeocodeShow"
      class="row align-items-center custom-location-buttons"
    >
      <label class="col-sm-3 control-label"> Lat* </label>
      <div class="col-sm-3 col-6">
        <number-input
          v-model="address.latitude"
          type="number"
          class="form-control"
          placeholder="Latitude"
          autocomplete="nope"
          :index="0"
          :disabled="isDisabled"
          :required="isAddressRequired"
          @paste-url="pasteUrl"
        />
      </div>

      <label class="col-sm-2 control-label mobile-mt-10"> Lng* </label>
      <div class="col-sm-3 col-6">
        <number-input
          v-model="address.longitude"
          type="number"
          class="form-control"
          placeholder="Longitude"
          autocomplete="nope"
          :index="1"
          :disabled="isDisabled"
          :required="isAddressRequired"
          @paste-url="pasteUrl"
        />
      </div>

      <div
        v-if="isCopyAddressAllow"
        class="col-sm-1 col-2 button-group pl-0"
      >
        <div class="location-button">
          <button
            type="button"
            class="btn copy-btn"
            @click="copyDataFromAddressForm(1)"
          >
            <i class="fa fa-copy" />
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="isLocationNameShow"
      class="row"
    >
      <label class="col-sm-3 control-label pt-10"> Location Name* </label>

      <div class="col-sm-8 col-10">
        <sanitized-input
          :id="`name-${pageId}`"
          v-model="address.name"
          placeholder="Name of the location"
          :disabled="isDisabled"
          :required="isAddressRequired"
        />
      </div>
    </div>

    <div class="row align-items-center mt-20">
      <label class="col-sm-3 control-label"> Google Place ID </label>

      <div class="col-sm-8 col-10">
        {{ address.googlePlaceId }}
      </div>
    </div>

    <VenueSelectors
      v-if="isVenuesSearch"
      :venues="venues"
      :disabled="isDisabled"
      :address="address"
      @selected="onVenueSelected"
    />

    <OrderingPinMap
      v-if="address.latitude && address.longitude"
      :venues="isVenuesSearch ? venues : null"
      :disabled="isDisabled"
      :address="address"
      :isPickup="isPickup"
      :additionalPins="additionalPins"
      :originalCoordinates="address.originalCoordinates"
      @selected="onVenueSelected"
      @drag-pin="showSaveCustomLocationDialog"
    />

    <div :class="isOffset ? 'row align-items-center' : 'form-group mt-20'">
      <div :class="isOffset ? 'col-sm-9 offset-sm-3 row' : 'form-action'">
        <div v-if="isIntersectionForm">
          <p-button
            :id="`btnAddressForm-${pageId}`"
            type="purple"
            round
            :disabled="isShowLoadingSpinner || isDisabled"
            @click="changeForm"
          >
            {{ address.type ? 'Address Form' : 'Intersection Form' }}
          </p-button>
        </div>

        <div v-if="isShowSaveLocation">
          <p-button
            :id="`btnSaveLocation-${pageId}`"
            type="purple"
            round
            :disabled="isShowLoadingSpinner || !isPhoneNumberValid || address.type || isDisabled"
            @click="showSaveCustomLocationDialog"
          >
            Save location
          </p-button>
        </div>

        <div v-if="isSwapLocation">
          <p-button
            :id="`btnSwitch-${pageId}`"
            type="purple"
            round
            :disabled="isShowLoadingSpinner || isDisabled"
            @click="swapAddress"
          >
            Switch&nbsp;&nbsp;
            <i
              class="fa fa-exchange"
              style="transform: rotate(90deg)"
            />
          </p-button>
        </div>

        <div>
          <p-button
            v-if="isResetShow"
            :id="`btnReset-${pageId}`"
            type="yellow"
            round
            :disabled="isShowLoadingSpinner || isDisabled"
            @click="initAddressForm(true)"
          >
            Reset
          </p-button>
        </div>
      </div>
    </div>

    <save-location-dialog
      :is-show-dialog="isShowCustomLocationDialog"
      @submit="makeUpdateData"
      @cancel="isShowCustomLocationDialog = false"
    />

    <update-custom-location-name-dialog
      :is-show-dialog="isShowUpdateCustomLocationNameDialog"
      @submit="updateCustomLocationName"
      @cancel="isShowUpdateCustomLocationNameDialog = false"
    />

    <miles-away-notice
      :is-show-dialog="open100MilesModal"
      @hide-dialog="open100MilesModal = false"
    />
  </fieldset>
</template>

<script lang="ts">
import { ref, Ref, computed, WritableComputedRef, watch, PropType } from 'vue';

import util from '@/Utils/util';
import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { useAddress } from '@/use/address';
import { sendGet } from '@/Utils/api';
import { AddressType, type MapMarker } from '@/models/address';
import { GeoLocationType, LocationDataType, CustomLocationType } from '@/models/address';
import { useSwalDialog } from '@/use/swalDialog';
import { useGlobal } from '@/use/global';
import { callerService } from '@/services/caller';

import SaveLocationDialog from './SaveLocationDialog.vue';
import UpdateCustomLocationNameDialog from './UpdateCustomLocationNameDialog.vue';
import MilesAwayNotice from './Dialogs/MilesAwayNotice.vue';
import VenueSelectors from './VenueSelectors.vue';
import OrderingPinMap from './OrderingPinMap.vue';
import { Venue, VenueSelection } from '@/models/venue'

export default {
  name: 'OrderingAddress',

  components: {
    SaveLocationDialog,
    UpdateCustomLocationNameDialog,
    MilesAwayNotice,
    VenueSelectors,
    OrderingPinMap,
  },

  props: {
    modelValue: {
      type: Object,
      default: () => ({
        name: '',
        streetAddress: '', // line 1
        streetAddress1: '', // intersection line 1
        streetAddress2: '', // line 2
        city: '',
        state: '',
        zip: '',
        country: '',
        latitude: null,
        longitude: null,
        googlePlaceId: null,
        type: false,
      }),
    },

    makeMarkerClickable: {
      type: Boolean,
      default: false,
    },

    pageId: {
      type: String,
      default: 'ordering-address-form',
    },

    index: {
      type: Number,
      default: 0,
    },

    isAddressRequired: {
      type: Boolean,
      default: true,
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    isGeocodeShow: {
      type: Boolean,
      default: false,
    },

    isShowSaveLocation: {
      type: Boolean,
      default: false,
    },

    isSwapLocation: {
      type: Boolean,
      default: true,
    },

    isIntersectionForm: {
      type: Boolean,
      default: true,
    },

    isShowClose: {
      type: Boolean,
      default: false,
    },

    isDraggable: {
      type: Boolean,
      default: false,
    },

    isShowForceUpdate: {
      type: Boolean,
      default: false,
    },

    isDragging: {
      type: Boolean,
      default: false,
    },

    isVenuesSearch: {
      type: Boolean,
      default: true,
    },

    isPhoneNumberValid: {
      type: Boolean,
      default: false,
    },

    isCustomLocationShow: {
      type: Boolean,
      default: true,
    },

    isCopyAddressAllow: {
      type: Boolean,
      default: true,
    },

    isOffset: {
      type: Boolean,
      default: false,
    },

    useGoogleAddress: {
      type: Boolean,
      default: false,
    },

    rideType: {
      type: String,
      default: '',
    },

    mapPinType: {
      type: String,
      default: '',
    },

    mapPinIndex: {
      type: Number,
      default: 0,
    },

    isMultiStopAddress: {
      type: Boolean,
      default: false,
    },

    isResetShow: {
      type: Boolean,
      default: true,
    },

    isLocationNameDialogShow: {
      type: Boolean,
      default: false,
    },

    isLocationNameShow: {
      type: Boolean,
      default: false,
    },

    anchorBound: {
      type: Object as PropType<google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral | null>,
      default: null,
    },

    show100MilesModal: {
      type: Boolean,
      default: false,
    },

    additionalPins: {
      type: Array as PropType<MapMarker[]>,
      default: () => ([]),
    },

    venues: {
      type: Array as PropType<Venue[]>,
      default: () => ([]),
    },
  },

  emits: [
    'change',
    'update:modelValue',
    'swap',
    'google-address',
    'close',
    'drag-start',
    'drag-end',
    'set-notes',
    'venueNote',
    'change-marker-position',
    'update-map-from-marker-click',
    'set-venue-places'
  ],

  setup: (props: any, context: any) => {
    const { emit } = context;

    const { isFamilyDashboard } = useDashboard();
    const { customerInfo, anchorBound: customerAnchorBound, setCustomerInfo } = useCustomer();
    const { showConfirmPopupDialog, showWarningPopupDialog, showErrorPopupDialog } = useSwalDialog();
    const { setLocationDataToSave } = useGlobal();
    const { compareDistances } = useAddress();

    const address: WritableComputedRef<CustomLocationType> = computed({
      get() {
        return props.modelValue;
      },
      set(value) {
        emit('update:modelValue', value);
      },
    });

    const open100MilesModal = ref(false);

    const updateMapFromMarkerClick = (markerType: string) => {
      emit('update-map-from-marker-click', markerType);
    };

    const isWarningShow = ref(false);
    const isShowLoadingSpinner = ref(false);
    const loadingMessage = ref('');
    const isShowCustomLocationDialog = ref(false);
    const isShowVenuesPlaceDialog = ref(false);
    const isShowUpdateCustomLocationNameDialog = ref(false);
    const customLocationOrderIndex = ref(0);

    const customLocationButtons: Ref<any> = ref({});

    const isFromCanada = computed(() => {
      return address.value.country === 'CA';
    });

    const isPickup = computed(() => props.index === AddressType.PickUp);
    const isDropoff = computed(() => props.index === AddressType.DropOff);

    const pasteUrl = (geocode: GeoLocationType) => {
      if (geocode) {
        const updatedAddress = {
          ...address.value,
          latitude: geocode.lat,
          longitude: geocode.lng,
        };

        emit('update:modelValue', updatedAddress);
        emit('change', props.index, updatedAddress);
      }
    };

    const checkAvailabilityToSearchVenuesPlace = (lat?: number | null, lng?: number | null) => {
      if (!props.isVenuesSearch) {
        return false;
      }

      if (!lat || !lng) {
        return false;
      }

      if (!customerInfo.value.id || !customerInfo.value.latitude || !customerInfo.value.longitude) {
        return true;
      }

      const isNearHome = compareDistances(lat, lng, customerInfo.value.latitude, customerInfo.value.longitude, 0.1, '<=');

      return !isNearHome;
    };

    const setAddressData = async (googleAddress?: CustomLocationType, googlePlaceData?: any) => {
      if (props.useGoogleAddress) {
        emit('google-address', googlePlaceData);
        return;
      }

      initAddressForm(!googleAddress);

      if (!googleAddress) {
        return;
      }

      const addressTypeToChangeCoordinates = googlePlaceData.types.find(
        (addressType: string) => addressType === 'establishment' || addressType === 'point_of_interest',
      );

      if (addressTypeToChangeCoordinates) {
        isWarningShow.value = true;
      }

      const intersection = googleAddress.intersection || [];

      const updatedAddress: any = {
        ...address.value,
        streetAddress2: '',
        streetAddress: googleAddress.streetAddress || '',
        city: googleAddress.city,
        state: googleAddress.state,
        zip: googleAddress.zip,
        country: googleAddress.country,
        latitude: googleAddress.latitude,
        longitude: googleAddress.longitude,
        googlePlaceId: googleAddress.googlePlaceId,
        name: googleAddress.name,
        googlePlaceTypes: googleAddress.googlePlaceTypes ?? [],
        addressComponents: googlePlaceData.address_components,
        originalCoordinates: {
          lat: googleAddress.latitude,
          lng: googleAddress.longitude,
        }
      };

      if (address.value.type) {
        updatedAddress.streetAddress = intersection[0] || googleAddress.streetAddress;
        updatedAddress.streetAddress1 = intersection[1] || '';
      } else {
        updatedAddress.streetAddress = updatedAddress.streetAddress || intersection.join(' & ');

        emit('set-venue-places', [], props.index);

        const isNeedToCheckVenuesPlace = checkAvailabilityToSearchVenuesPlace(googleAddress.latitude, googleAddress.longitude);
        if (isNeedToCheckVenuesPlace) {
          await getVenuesPlaces(Number(updatedAddress.latitude), Number(updatedAddress.longitude));
        }
      }
      if (props.show100MilesModal) {
        if (
          compareDistances(
            updatedAddress.latitude,
            updatedAddress.longitude,
            customerInfo.value.latitude,
            customerInfo.value.longitude,
            100,
            '>=',
          )
        ) {
          open100MilesModal.value = true;
        }
      }

      emit('update:modelValue', updatedAddress);
      emit('change', props.index, updatedAddress);
    };

    const initAddressForm = (isAll: boolean = false) => {
      isWarningShow.value = false;

      if (isAll) {
        const emptyAddress = {
          name: '',
          streetAddress: '', // line 1
          streetAddress1: '', // intersection line 1
          streetAddress2: '', // line 2
          city: '',
          state: '',
          zip: '',
          country: '',
          latitude: null,
          longitude: null,
          googlePlaceId: null,
          type: false,
        };

        customLocationButtons.value?.initAddress && customLocationButtons.value.initAddress();

        emit('update:modelValue', emptyAddress);
        emit('change', props.index, emptyAddress);
        emit('venueNote', '', '', props.index);
        emit('set-venue-places', [], props.index);
      }
    };

    const changeForm = () => {
      const updatedAddress = {
        ...address.value,
        streetAddress1: '',
        type: !address.value.type,
      };

      emit('update:modelValue', updatedAddress);
      emit('change', props.index, updatedAddress);
    };

    const showSaveCustomLocationDialog = () => {
      if (isFamilyDashboard.value || !props.isShowSaveLocation) {
        return;
      }

      isShowCustomLocationDialog.value = true;
    };

    const swapAddress = () => {
      emit('swap');
    };

    const copyDataFromAddressForm = (category: number /* 0: address, 1: geocode */) => {
      const btnComponentList = document.querySelectorAll(`#${props.pageId} .copy-btn`) as NodeListOf<HTMLButtonElement>;
      btnComponentList.forEach((component: HTMLButtonElement) => {
        component.blur();
      });

      copyDataFromAddress(address.value, category);
    };

    // Copy address or geocde from address object regarding with category
    // category => 0: copy address, 1: copy geocode
    const copyDataFromAddress = (address: CustomLocationType, category: number) => {
      let copyString = '';
      navigator.clipboard.writeText(copyString);

      if (category === 0) {
        // generate copy text with address
        let streetAddress = '';
        if (address.streetAddress) {
          // if address type is interaction, merge streetAddress1 and streetAddress2
          if (address.type) {
            streetAddress = address.streetAddress1 ? `${address.streetAddress} & ${address.streetAddress1}` : `${address.streetAddress}`;
          } else {
            streetAddress = address.streetAddress;
          }
        }
        if (streetAddress && address.city && address.state && address.zip) {
          copyString = `${streetAddress}, ${address.city}, ${address.state}, ${address.zip}`;
        }
      } else {
        // generate copy text with geocode
        if (address.latitude && address.longitude) {
          copyString = `${address.latitude}, ${address.longitude}`;
        }
      }

      // if generate the copystring, save into clipboard
      if (copyString) {
        navigator.clipboard.writeText(copyString);
      } else {
        // if address is empty, show error message
        showErrorPopupDialog({
          text: `You need to fill the form before you copy ${category === 0 ? 'address' : 'coordinates'}.`,
        });
      }
    };

    const makeUpdateData = async (selectedLocation: any) => {
      isShowCustomLocationDialog.value = false;

      if (selectedLocation.type === 'locationType') {
        const type = selectedLocation.value;
        if (type === 'home') {
          if (customerInfo.value.streetAddress) {
            const isOverwriteHomeAddress = await showConfirmPopupDialog({
              title: 'Overwrite Home Address?',
              confirmButtonText: 'Overwrite',
            });

            if (!isOverwriteHomeAddress) {
              return;
            }
          }

          setLocationDataToSave({
            type: 'home',
            updatedAddress: address.value,
          });
        } else {
          const str = type.substr(-1);
          const ind = ['a', 'b', 'c'].indexOf(str) + 1;
          if (customerInfo.value[str + 'Location']) {
            const isOverwriteCustomLocation = await showConfirmPopupDialog({
              title: `Overwrite Custom Location #${ind + 2}?`,
              confirmButtonText: 'Overwrite',
            });

            if (!isOverwriteCustomLocation) {
              return;
            }
          }

          setLocationDataToSave({
            type: 'custom' + ind,
            updatedAddress: address.value,
          });
        }
      } else if (selectedLocation.type === 'locationOrder') {
        const customAddress = customerInfo.value.customAddresses.find((address: any) => address.order === selectedLocation.value);

        if (customAddress) {
          const isOverwriteCustomLocation = await showConfirmPopupDialog({
            title: `Overwrite Custom Location *${selectedLocation.value}?`,
            confirmButtonText: 'Overwrite',
          });

          if (!isOverwriteCustomLocation) {
            return;
          }

          const updatedAddress = {
            ...address.value,
            id: customAddress.id,
            index: customAddress.index,
          };

          setLocationDataToSave({
            order: selectedLocation.value,
            updatedAddress,
          });
        } else {
          setLocationDataToSave({
            order: selectedLocation.value,
            updatedAddress: address.value,
          });
        }
      }
    };

    const getVenuesPlaces = async (lat: number, lng: number) => {
      loadingMessage.value = 'Loading venues places...';

      isShowLoadingSpinner.value = true;
      try {
        const response = await sendGet('venues', {
          lat,
          lng,
          rideType: props.rideType,
          includeAccessPoints: true,
        });
        isShowLoadingSpinner.value = false;
        emit('set-venue-places', response.data || [], props.index);
      } catch (error) {
        isShowLoadingSpinner.value = false;
        emit('set-venue-places', [], props.index);
      }
    };

    // parse venues address
    // venues address components has difference format with normal address
    const getAirportAddress = (address_components: any[]) => {
      const addressObject: LocationDataType = {
        streetAddress: '',
        city: '',
        state: '',
        zip: '',
        country: '',
      };

      address_components.map((address: any) => {
        address.types.map((type: any) => {
          if (type === 'country') {
            addressObject.country = address.short_name;
          }
          if (type === 'administrative_area_level_2') {
            addressObject.city = address.short_name;
          }
          if (type === 'political') {
            addressObject.city = addressObject.city || address.short_name;
          }
          if (type === 'administrative_area_level_1') {
            addressObject.state = address.short_name;
          }
          if (type === 'postal_code') {
            addressObject.zip = address.short_name;
          }
          return type;
        });
        return address;
      });

      return addressObject;
    };

    const onVenueSelected = (selection: VenueSelection) => {
      const { venue, zone, location, name, position } = selection;

      const instruction = location?.note || zone?.info || venue?.introduction || '';

      emit('venueNote', name, isPickup.value ? instruction : '', props.index, selection);

      if (zone || location) {
        const airportAddress = address.value.addressComponents ? getAirportAddress(address.value.addressComponents) as LocationDataType : address.value;
        airportAddress.streetAddress = venue?.zones?.length
          ? venue.name
          : address.value.streetAddress || venue?.name || '';

        const updatedAddress = {
          ...address.value,
          ...airportAddress,
          latitude: position?.lat ?? (location || zone)?.latitude,
          longitude: position?.lng ?? (location || zone)?.longitude,
          locationId: location?.id,
          zoneId: zone?.id,
          venueId: venue?.id,
          entranceName: name,
        };

        emit('update:modelValue', updatedAddress);
        emit('change', props.index, updatedAddress);
      } else {
        const updatedAddress = {
          ...address.value,
          latitude: position?.lat ?? address.value.latitude,
          longitude: position?.lng ?? address.value.longitude,
          locationId: null,
          zoneId: null,
          venueId: null,
          entranceName: name,
        };

        emit('update:modelValue', updatedAddress);
        emit('change', props.index, updatedAddress);
      }
    };

    const changeAddress = async (customLocation: CustomLocationType) => {
      initAddressForm();

      const updatedAddress = {
        ...customLocation,
        originalCoordinates: {
          lat: customLocation.latitude,
          lng: customLocation.longitude,
        },
      };

      emit('set-venue-places', [], props.index);
      emit('venueNote', '', '', props.index);

      const isNeedToCheckVenuesPlace = checkAvailabilityToSearchVenuesPlace(
        customLocation.latitude,
        customLocation.longitude,
      );
      if (isNeedToCheckVenuesPlace) {
        await getVenuesPlaces(Number(customLocation.latitude), Number(customLocation.longitude));
      }

      emit('update:modelValue', updatedAddress);
      emit('change', props.index, updatedAddress);
    };

    const loadAddress = (isLoading: boolean, message: string = '') => {
      isShowLoadingSpinner.value = isLoading;
      loadingMessage.value = message;
    };

    const closeAddressSection = () => {
      emit('close', props.index);
    };

    const forceUpdateStop = () => {
      emit('force-update-stop', props.index);
    };

    const dragBtnOver = () => {
      emit('drag-start');
    };

    const dragBtnRelease = () => {
      emit('drag-end');
    };

    const showUpdateCustomLocationNameDialog = (locationOrderIndex: number) => {
      customLocationOrderIndex.value = locationOrderIndex;
      isShowUpdateCustomLocationNameDialog.value = true;
    };

    const updateCustomLocationName = async (locationName: string) => {
      isShowUpdateCustomLocationNameDialog.value = false;

      isShowLoadingSpinner.value = true;
      loadingMessage.value = 'Updating Custom Location Name...';

      if (address.value.id) {
        // only custom locations *1-*8 has an id because they are in a separate table callerAddresses
        await callerService.updateCustomerAddress({
          id: Number(address.value.id!),
          data: {
            name: locationName,
          },
        });

        const customLocationList = customerInfo.value.customAddresses;
        customLocationList[customLocationOrderIndex.value - 1].name = locationName;

        setCustomerInfo({
          ...customerInfo.value,
          customAddresses: customLocationList,
        });
      } else {
        // Custom Location #3 ~ #5
        const locationLabelPrefixes = ['a', 'b', 'c'];
        const locationLabelPrefix = locationLabelPrefixes[customLocationOrderIndex.value - 1];

        await callerService.updateCaller({
          callerId: customerInfo.value.id,
          data: {
            [`${locationLabelPrefix}Name`]: locationName,
          },
        });

        setCustomerInfo({
          ...customerInfo.value,
          [`${locationLabelPrefix}Name`]: locationName,
        });
      }

      address.value.name = locationName;
      isShowLoadingSpinner.value = false;
    };

    watch(
      address,
      (newValue: any, oldValue: any) => {
        const isAddressChanged = util.isAddressChanged(newValue, oldValue);
        if (props.isMultiStopAddress) {
          if (isAddressChanged && !props.isDragging) {
            showWarningPopupDialog({
              text: 'Please adjust the pin so driver can actually drive to it.<br />i.e. The pin should not be in the middle of a building.',
              type: 'html',
            });
          }
        }

        if (!isAddressChanged && !props.isDragging) {
          emit('change-marker-position', newValue.latitude, newValue.longitude, props.index, false);
        }

        if (!newValue.latitude || !newValue.longitude) {
          newValue.originalCoordinates = null;
        }
      },
      {
        deep: true,
      },
    );

    return {
      address,
      isWarningShow,
      isShowLoadingSpinner,
      loadingMessage,
      isShowCustomLocationDialog,
      isShowVenuesPlaceDialog,
      customerAnchorBound,
      isFromCanada,
      isShowUpdateCustomLocationNameDialog,
      customLocationButtons,
      open100MilesModal,

      pasteUrl,
      setAddressData,
      initAddressForm,
      changeForm,
      showSaveCustomLocationDialog,
      swapAddress,
      copyDataFromAddressForm,
      makeUpdateData,
      changeAddress,
      loadAddress,
      closeAddressSection,
      forceUpdateStop,
      dragBtnOver,
      dragBtnRelease,
      updateCustomLocationName,
      showUpdateCustomLocationNameDialog,
      updateMapFromMarkerClick,

      isPickup,
      isDropoff,
      onVenueSelected,
    };
  },
};
</script>

<style lang="scss">
.ordering-address-form {
  .form-action {
    display: flex;
    align-items: center;
    justify-content: center;
    @media (max-width: 576px) {
      flex-wrap: wrap;
      justify-content: space-between;
      div {
        flex: 1;
        margin-bottom: 5px;
        button {
          width: 100% !important;
        }
      }
    }
  }
  .venues-notification {
    font-size: 16px;
    color: red;
  }
  .section-header {
    position: relative;
    .section__headerbtn {
      min-width: unset;
      padding: 5px !important;
      height: 30px;
      line-height: 25px;
      border: 0;
      background: transparent;
      outline: none;
      cursor: pointer;
      font-size: 16px !important;
      position: absolute;
      &.close-btn {
        top: -10px;
        right: 0;
      }
      &.drag-btn {
        top: -9px;
        right: 30px;
        font-size: 14px !important;
      }
      &.force-btn {
        top: -10px;
        right: 60px;
        font-size: 14px !important;
      }
      &:hover {
        color: #eb5e28;
      }
    }
  }
}
</style>
